import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Branding assets
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function MiningTable12() {
  const pdfRef = useRef();

  const data = [
    {
      age_group_label: "14–18",
      female: 0,
      male: 3,
      female_pct: 0.0,
      male_pct: 100.0,
    },
    {
      age_group_label: "19–21",
      female: 9,
      male: 43,
      female_pct: 17.31,
      male_pct: 82.69,
    },
    {
      age_group_label: "22–24",
      female: 22,
      male: 58,
      female_pct: 27.5,
      male_pct: 72.5,
    },
    {
      age_group_label: "25–34",
      female: 95,
      male: 388,
      female_pct: 19.67,
      male_pct: 80.33,
    },
    {
      age_group_label: "35–44",
      female: 89,
      male: 588,
      female_pct: 13.15,
      male_pct: 86.85,
    },
    {
      age_group_label: "45–54",
      female: 78,
      male: 541,
      female_pct: 12.6,
      male_pct: 87.4,
    },
    {
      age_group_label: "55–64",
      female: 40,
      male: 384,
      female_pct: 9.43,
      male_pct: 90.57,
    },
    {
      age_group_label: "65–99",
      female: 15,
      male: 100,
      female_pct: 13.04,
      male_pct: 86.96,
    },
  ];

  // Chart Data
  const chartData = {
    labels: data.map((d) => d.age_group_label),
    datasets: [
      {
        label: "Female Employees",
        data: data.map((d) => d.female),
        backgroundColor: "rgba(236, 72, 153, 0.85)",
        borderRadius: 6,
      },
      {
        label: "Male Employees",
        data: data.map((d) => d.male),
        backgroundColor: "rgba(14, 165, 233, 0.85)",
        borderRadius: 6,
      },
    ],
  };

  const ratioChartData = {
    labels: data.map((d) => d.age_group_label),
    datasets: [
      {
        label: "Female Share (%)",
        data: data.map((d) => d.female_pct),
        backgroundColor: "rgba(34, 197, 94, 0.85)",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "👷 Utah Mining Workforce Distribution by Age & Gender (2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        title: { display: true, text: "Number of Employees" },
      },
    },
  };

  const ratioOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Female Share (%) by Age Group",
        font: { size: 14, weight: "bold" },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        max: 35,
        title: { display: true, text: "Female Employment %" },
      },
    },
  };

  // Insights
  const totalFemale = data.reduce((sum, d) => sum + d.female, 0);
  const totalMale = data.reduce((sum, d) => sum + d.male, 0);
  const avgFemalePct = (
    data.reduce((sum, d) => sum + d.female_pct, 0) / data.length
  ).toFixed(1);
  const topFemaleGroup = data.reduce((max, d) =>
    d.female_pct > max.female_pct ? d : max
  );

  // PDF Export
  const downloadPDF = async () => {
    const input = pdfRef.current;
    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth - 10;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Header
    pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
    pdf.setFontSize(26);
    pdf.text("UTahData.org", 95, 22);
    pdf.line(10, 35, 285, 35);
    pdf.addImage(imgData, "PNG", 5, 38, imgWidth, imgHeight);

    // Footer
    const footerY = 190;
    pdf.line(10, footerY - 5, 285, footerY - 5);
    pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
    pdf.setFontSize(10);
    pdf.text("Built by", 55, footerY + 10);
    pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
    pdf.text("using", 112, footerY + 10);
    pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
    pdf.text("20250916.v3.198", 148, footerY + 10);
    pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 10, 205);
    pdf.text(`From: ${window.location.href}`, 150, 205);

    pdf.save("utah_mining_age_gender_distribution.pdf");
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-pink-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Utah Mining Workforce Distribution by Age & Gender (2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Female Employees
            </p>
            <p className="text-xl font-bold text-blue-800">{totalFemale}</p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Average Female Share
            </p>
            <p className="text-xl font-bold text-green-800">{avgFemalePct}%</p>
          </div>
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Peak Female Group
            </p>
            <p className="text-lg font-bold text-pink-800">
              {topFemaleGroup.age_group_label} ({topFemaleGroup.female_pct}%)
            </p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Female</th>
                  <th className="p-2">Male</th>
                  <th className="p-2">Female %</th>
                  <th className="p-2">Male %</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr
                    key={i}
                    className={`${
                      row.age_group_label === topFemaleGroup.age_group_label
                        ? "bg-green-50 font-semibold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{row.age_group_label}</td>
                    <td className="p-2 text-pink-700">{row.female}</td>
                    <td className="p-2 text-blue-700">{row.male}</td>
                    <td className="p-2 text-green-700">{row.female_pct}%</td>
                    <td className="p-2 text-gray-700">{row.male_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Gender Count */}
          <div className="bg-white rounded-lg shadow-md p-4 flex-grow h-[400px]">
            <Bar data={chartData} options={options} />
          </div>

          {/* Female Ratio
          <div className="bg-white rounded-lg shadow-md p-4 flex-grow h-[400px]">
            <Bar data={ratioChartData} options={ratioOptions} />
          </div> */}
        </div>

        {/* Summary */}
        <p className="text-center text-gray-700 mt-4">
          👷 The mining workforce in Utah is predominantly male across all age
          groups, though younger workers (22–24) show the highest female
          participation at <b>{topFemaleGroup.female_pct}%</b>.
        </p>
      </div>

      {/* Download PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
